import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LayoutTemplate, Plus, Loader2, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function Templates() {
  const [cats, setCats] = useState(null);
  const [tpls, setTpls] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const [c, t] = await Promise.all([api.get("/task-categories"), api.get("/task-templates")]);
    setCats(c.data);
    setTpls(t.data);
    const d = {};
    c.data.forEach((cat) => { d[cat.key] = (t.data[cat.key] || []).join("\n"); });
    setDrafts(d);
  };
  useEffect(() => { load().catch(() => setCats([])); }, []);

  const saveTpl = async (key) => {
    setSavingKey(key);
    try {
      const subtasks = (drafts[key] || "").split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put(`/task-templates/${key}`, { subtasks });
      toast.success("Sjabloon opgeslagen");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingKey(null); }
  };

  const addCat = async () => {
    if (!newLabel.trim()) return;
    setAdding(true);
    try {
      await api.post("/task-categories", { label: newLabel.trim() });
      setNewLabel("");
      toast.success("Categorie toegevoegd");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setAdding(false); }
  };

  const delCat = async (cat) => {
    if (!window.confirm(`Categorie "${cat.label}" verwijderen?`)) return;
    try { await api.delete(`/task-categories/${cat.id}`); toast.success("Verwijderd"); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <h1 className="font-display font-bold text-3xl tracking-tight">Taaksjablonen</h1>
        <p className="text-muted-foreground mt-1">Standaard-checklists per categorie. Per huis kun je deze aanpassen op de woningpagina.</p>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 shadow-sm mb-6 flex items-end gap-3 flex-wrap" data-testid="add-category-card">
        <div className="flex-1 min-w-[220px]">
          <label className="text-sm font-medium">Nieuwe categorie</label>
          <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Bijv. Tuinonderhoud" className="mt-1.5" data-testid="new-category-input" />
        </div>
        <Button onClick={addCat} disabled={adding || !newLabel.trim()} className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-category-btn">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Toevoegen
        </Button>
      </div>

      {!cats ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {cats.map((cat) => (
            <div key={cat.key} className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid={`template-card-${cat.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <LayoutTemplate className="h-4 w-4 text-primary" />
                  <h2 className="font-display font-semibold text-lg">{cat.label}</h2>
                  {!cat.builtin && <span className="text-[10px] uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded">eigen</span>}
                </div>
                {!cat.builtin && (
                  <Button size="icon" variant="ghost" onClick={() => delCat(cat)} className="text-destructive h-8 w-8" data-testid={`delete-category-${cat.key}`}><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
              <label className="text-xs text-muted-foreground">Subtaken (één per regel)</label>
              <Textarea
                value={drafts[cat.key] ?? ""}
                onChange={(e) => setDrafts({ ...drafts, [cat.key]: e.target.value })}
                rows={7}
                className="mt-1.5 font-mono text-sm"
                data-testid={`template-textarea-${cat.key}`}
              />
              <div className="flex justify-end mt-3">
                <Button size="sm" onClick={() => saveTpl(cat.key)} disabled={savingKey === cat.key} className="bg-primary hover:bg-primary/90 gap-2" data-testid={`save-template-${cat.key}`}>
                  {savingKey === cat.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Opslaan
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
